import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Calendar, Scale, MapPin } from "lucide-react";

interface TimeSlot {
  dateRange: {
    from: Date;
    to?: Date;
  };
  startTime: string;
  endTime: string;
  availablePortions: number;
  recurring?: {
    days: string[];
    until: Date;
  };
}

interface ReviewProps {
  formData: {
    name: string;
    price: string;
    description: string;
    images: string[];
    availabilitySlots: TimeSlot[];
    deliveryAvailable: boolean;
    deliveryFee: string;
    cuisine_type: string;
    meal_type: string;
    dietary_tags: string[];
    allergens: string[];
    ingredients: string[];
    calories: string;
    carbs: string;
    protein: string;
    fat: string;
    hasLimitedPortions?: boolean;
    available_portions?: string;
    preparation_type: "scheduled" | "on_demand";
    prep_time_hours?: number;
    cutoff_time?: string;
    max_daily_orders?: number;
    pickup_window_start?: string;
    pickup_window_end?: string;
    available_days: string[];
    pickup_instructions?: string;
    portion_size_value?: string;
    portion_size_unit?: string;
    use_custom_pickup_address?: boolean;
    pickup_address?: string;
    pickup_city?: string;
    pickup_state?: string;
    pickup_postal_code?: string;
  };
  onSubmit: () => void;
  onBack: () => void;
}

export const StepReview = ({ formData, onSubmit, onBack }: ReviewProps) => {
  const formatTime = (time: string) => {
    if (!time) return "";
    
    // Handle cases where we already have AM/PM format
    if (time.includes("AM") || time.includes("PM")) return time;
    
    // Parse the 24-hour time
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const formatDateRange = (slot: TimeSlot) => {
    const { from, to } = slot.dateRange;
    if (!to || format(from, 'MMM d, yyyy') === format(to, 'MMM d, yyyy')) {
      return format(from, "MMM d, yyyy");
    }
    return `${format(from, "MMM d")} - ${format(to, "MMM d, yyyy")}`;
  };

  const formatMealType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatAvailabilitySlot = (slot: TimeSlot) => {
    if (slot.recurring) {
      return `${slot.recurring.days.join(", ")} until ${format(new Date(slot.recurring.until), "MMM d, yyyy")} • ${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
    }
    return `${formatDateRange(slot)} • ${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
  };

  const formatPreparationType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
  };

  return (
    <div className="space-y-6 px-0">
      <div className="text-center">
        <h1 className="text-[28px] font-medium mb-3 text-black">Review Meal</h1>
        <p className="text-[#6B7280] text-base">
          Review the details of your meal listing before publishing.
        </p>
      </div>

      {formData.images.length > 0 && (
        <img
          src={formData.images[0]}
          alt={formData.name}
          className="w-full h-48 object-cover rounded-xl"
        />
      )}

      <div className="space-y-6">
        <div className="space-y-2.5">
          <Label className="text-black font-medium text-base">Meal Name</Label>
          <Input
            value={formData.name}
            readOnly
            className="h-12 rounded-xl border-[#E5E5E5] bg-[#F9FAFB] text-[#6B7280]"
          />
        </div>

        <div className="space-y-2.5">
          <Label className="text-black font-medium text-base">Price ($)</Label>
          <Input
            value={formData.price}
            readOnly
            className="h-12 rounded-xl border-[#E5E5E5] bg-[#F9FAFB] text-[#6B7280]"
          />
        </div>

        <div className="space-y-2.5">
          <Label className="text-black font-medium text-base">Description</Label>
          <Textarea
            value={formData.description}
            readOnly
            className="min-h-[120px] rounded-xl border-[#E5E5E5] bg-[#F9FAFB] text-[#6B7280]"
          />
        </div>

        <div className="space-y-2.5">
          <Label className="text-black font-medium text-base">Preparation Type</Label>
          <div className="p-4 rounded-xl border border-[#E5E5E5] bg-[#F9FAFB]">
            <p className="text-[#6B7280]">{formatPreparationType(formData.preparation_type)}</p>
          </div>
        </div>

        {formData.preparation_type === "scheduled" ? (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Available Time Slots</Label>
            <ScrollArea className="h-[200px] rounded-xl border border-[#E5E5E5] bg-[#F9FAFB] p-4">
              <div className="space-y-2">
                {formData.availabilitySlots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-[#E5E5E5]"
                  >
                    <Clock className="h-4 w-4 text-[#6B7280]" />
                    <span className="text-sm text-[#6B7280]">
                      {formatAvailabilitySlot(slot)}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <div className="space-y-6 rounded-xl border border-[#E5E5E5] bg-[#F9FAFB] p-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className="text-black font-medium text-base">Preparation Time</Label>
                <p className="text-[#6B7280] mt-1">
                  {formData.prep_time_hours} hours
                </p>
              </div>
              
              <div>
                <Label className="text-black font-medium text-base">Maximum Daily Orders</Label>
                <p className="text-[#6B7280] mt-1">
                  {formData.max_daily_orders} orders
                </p>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className="text-black font-medium text-base">Order Cutoff Time</Label>
                <p className="text-[#6B7280] mt-1">
                  {formatTime(formData.cutoff_time || "")}
                </p>
              </div>

              <div>
                <Label className="text-black font-medium text-base">Pickup Window</Label>
                <p className="text-[#6B7280] mt-1">
                  {formatTime(formData.pickup_window_start || "")} - {formatTime(formData.pickup_window_end || "")}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-black font-medium text-base">Available Days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.available_days.map((day) => (
                  <Badge 
                    key={day}
                    variant="secondary"
                    className="bg-white text-[#6B7280] border border-[#E5E5E5]"
                  >
                    {day}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {formData.deliveryAvailable && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Delivery Fee ($)</Label>
            <Input
              value={formData.deliveryFee}
              readOnly
              className="h-12 rounded-xl border-[#E5E5E5] bg-[#F9FAFB] text-[#6B7280]"
            />
          </div>
        )}

        {/* Custom Pickup Address Section */}
        {formData.use_custom_pickup_address && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Custom Pickup Address</Label>
            <div className="p-4 rounded-xl border border-[#E5E5E5] bg-[#F9FAFB] space-y-1">
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-[#6B7280] mr-2 mt-0.5" />
                <div>
                  <p className="text-[#6B7280]">{formData.pickup_address}</p>
                  <p className="text-[#6B7280]">
                    {formData.pickup_city}, {formData.pickup_state} {formData.pickup_postal_code}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {formData.pickup_instructions && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Pickup Instructions</Label>
            <div className="p-4 rounded-xl border border-[#E5E5E5] bg-[#F9FAFB]">
              <p className="text-[#6B7280]">{formData.pickup_instructions}</p>
            </div>
          </div>
        )}

        <div className="space-y-2.5">
          <Label className="text-black font-medium text-base">Cuisine Type</Label>
          <Input
            value={formData.cuisine_type}
            readOnly
            className="h-12 rounded-xl border-[#E5E5E5] bg-[#F9FAFB] text-[#6B7280]"
          />
        </div>

        {formData.dietary_tags.length > 0 && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Dietary Tags</Label>
            <div className="flex flex-wrap gap-2">
              {formData.dietary_tags.map((tag) => (
                <Badge 
                  key={tag} 
                  variant="secondary"
                  className="bg-[#F9FAFB] text-[#6B7280] border border-[#E5E5E5]"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {formData.allergens.length > 0 && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Allergens</Label>
            <div className="flex flex-wrap gap-2">
              {formData.allergens.map((allergen) => (
                <Badge 
                  key={allergen} 
                  variant="destructive"
                  className="bg-[#FEE2E2] text-[#DC2626] border-none"
                >
                  {allergen}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2.5">
          <Label className="text-black font-medium text-base">Ingredients</Label>
          <div className="flex flex-wrap gap-2">
            {formData.ingredients.map((ingredient) => (
              <Badge 
                key={ingredient} 
                variant="outline"
                className="border-[#E5E5E5] text-[#6B7280]"
              >
                {ingredient}
              </Badge>
            ))}
          </div>
        </div>

        {(formData.calories || formData.carbs || formData.protein || formData.fat) && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Nutritional Information (per serving)</Label>
            <div className="grid grid-cols-2 gap-4">
              {formData.calories && (
                <div className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E5E5]">
                  <span className="text-sm text-[#6B7280]">Calories: {formData.calories}</span>
                </div>
              )}
              {formData.carbs && (
                <div className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E5E5]">
                  <span className="text-sm text-[#6B7280]">Carbs: {formData.carbs}g</span>
                </div>
              )}
              {formData.protein && (
                <div className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E5E5]">
                  <span className="text-sm text-[#6B7280]">Protein: {formData.protein}g</span>
                </div>
              )}
              {formData.fat && (
                <div className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E5E5]">
                  <span className="text-sm text-[#6B7280]">Fat: {formData.fat}g</span>
                </div>
              )}
            </div>
          </div>
        )}

        {formData.portion_size_value && formData.portion_size_unit && (
          <div className="space-y-2.5">
            <Label className="text-black font-medium text-base">Portion Size</Label>
            <div className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E5E5] flex items-center gap-2">
              <Scale className="h-4 w-4 text-[#6B7280]" />
              <span className="text-sm text-[#6B7280]">
                {formData.portion_size_value} {formData.portion_size_unit}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="px-8 py-3 h-12 rounded-full text-base font-medium border-[#E5E5E5] hover:bg-[#F9FAFB] text-black"
        >
          Back
        </Button>
        <Button
          onClick={onSubmit}
          className="bg-black hover:bg-black/90 text-white px-8 py-3 h-12 rounded-full text-base font-medium transition-colors"
        >
          Create Meal
        </Button>
      </div>
    </div>
  );
};
